import React, {useState} from 'react';
import {useQuery} from "react-query";
import ngage from "services/ngage";
import pluralize, {singular} from 'pluralize';
import Page from "components/Page";
import {Table, TableBody, TableData, TableHead, TableHeader, TableRow} from "components/Table";
import {LinkIcon} from "@heroicons/react/outline";
import {LinkIcon as LinkIconSolid, PencilIcon, PlusIcon, SortAscendingIcon, TrashIcon} from "@heroicons/react/solid";
import Button from "components/Button";
import {toTitleCase, truncate} from "services/string";
import Dropdown from "components/Dropdown";
import {Link} from "types/Link";
import {useNavigate} from "react-router-dom";
import DeleteLink from '../Delete';
import BooleanIndicator from "components/BooleanIndicator";
import Icon from "components/Icon";
import ReorderLink from "../Reorder";
import {useOperator} from "../../../context/OperatorContext";

// Types ----

type LinkModalAction = {
  link: Link
  action: 'reorder' | 'delete'
}

// Helpers ----

function urlDisplay(url: string, termFor: (term: string) => string) {
  const [scheme, path] = url.split(':');

  if (scheme.startsWith('mailto')) return `Email (${path})`;

  if (scheme.startsWith('tel')) return `Call (${path})`;

  if (scheme.startsWith('http')) {
    const url = path.substring(2);
    const urlWithoutQuery = url.split('?')[0];

    return `URL (${truncate(urlWithoutQuery, 35)})`;
  }

  if (scheme.startsWith('ngage')) {
    const parts = path.substring(2).split('/');

    if (parts.length > 1) {
      return `App Content (${toTitleCase(singular(termFor(parts[0])))})`
    }

    return `App Content (${toTitleCase(termFor(parts[0]))})`
  }

  return 'Unknown';
}

// Get the display name for a link category
function categoryName(category: string) {
  const categoryNames: Record<string, string> = {
    'default': 'Overview',
    'wallet-tickets': 'Tickets',
    'wallet-memberships': 'Memberships',
    'wallet-vouchers': 'Vouchers',
  }

  return categoryNames[category] ?? 'Overview'
}

// Component ----

export default function LinkList() {
  const navigate = useNavigate();
  const {termFor} = useOperator();

  // Keep track of the current modal action being carried out
  const [modalAction, setModalAction] = useState<LinkModalAction | null>(null);

  // General Links
  const [generalLinksPage, setGeneralLinksPage] = useState<number>(1);

  const generalLinkCriteria = {
    page: generalLinksPage,
    attached: false,
    category: 'default,overview',
    sort: '-featured_at,order'
  }

  const {data: generalLinks, isLoading: isLoadingGeneralLinks} = useQuery(
    ['links', generalLinkCriteria],
    () => ngage.links.filtered(generalLinkCriteria),
    {keepPreviousData: true}
  );

  // Screen-specific Links
  const [screenLinksPage, setScreenLinksPage] = useState<number>(1);

  const screenLinkCriteria = {
    page: screenLinksPage,
    attached: false,
    category: 'wallet-tickets,wallet-memberships,wallet-vouchers',
    sort: '-featured_at,order'
  }

  const {data: screenLinks, isLoading: isLoadingScreenLinks} = useQuery(
    ['links', screenLinkCriteria],
    () => ngage.links.filtered(screenLinkCriteria),
    {keepPreviousData: true}
  );

  const linkCount = (generalLinks?.meta.total || 0) + (screenLinks?.meta.total || 0)
  const meta = [
    {icon: LinkIconSolid, value: pluralize('link', linkCount, true)}
  ]
  
  const breadcrumbs = {
    'App Content': '/',
    'Featured': '/',
    'Links': '/links'
  }

  const actions = (<>
    <Button icon={PlusIcon} onClick={() => navigate('/links/new')}>New</Button>
  </>)

  return (
    <Page
      title="Links"
      subTitle={`Interactive links to maximise engagement`}
      breadcrumbs={breadcrumbs}
      meta={meta}
      actions={actions}
    >

      <div>
        <h2 className="mb-2 text-lg leading-6 font-medium text-gray-900">
          General Links
        </h2>

        {/* General Links */}
        <Table paginationData={generalLinks} setPage={setGeneralLinksPage}>
          <TableHead>
            <TableRow>
              <TableHeader>Text</TableHeader>
              <TableHeader>Link To</TableHeader>
              <TableHeader>Category</TableHeader>
              <TableHeader alignment={TableHeader.alignments.CENTER}>Published</TableHeader>
              <TableHeader alignment={TableHeader.alignments.CENTER}>Featured</TableHeader>
              <TableHeader/>
            </TableRow>
          </TableHead>

          <TableBody isLoading={isLoadingGeneralLinks}>

            {/* Empty */}
            {(!generalLinks || generalLinks.links.length < 1) &&
              <TableRow index={0}>
                <TableData colSpan={6}>
                  <div className="py-4 flex flex-col justify-center items-center">
                    <LinkIcon className="h-8 w-8 text-gray-400 mb-2"/>
                    <span className="text-sm font-medium text-gray-600">No links</span>
                  </div>
                </TableData>
              </TableRow>
            }

            {/* Link Rows */}
            {generalLinks?.data.map((link, index) => (
              <TableRow key={link.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      {/* TODO: Convert to component */}
                      <Dropdown position='left'>
                        {[
                          {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/links/${link.id}/edit`)},
                          {name: 'Reorder', icon: SortAscendingIcon, onClick: () => setModalAction({link, action: 'reorder'})},
                          {name: 'Delete', icon: TrashIcon, onClick: () => setModalAction({link, action: 'delete'})},
                        ]}
                      </Dropdown>
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      {link.icon ?
                        <Icon name={link.icon} className="h-5 w-5 mr-2 text-gray-500"/> :
                        <LinkIcon className="h-5 w-5 mr-2 text-gray-500"/>
                      }
                      {truncate(link.text, 35)}
                    </div>
                  </div>
                </TableData>
                <TableData>{urlDisplay(link.url, termFor)}</TableData>
                <TableData>{categoryName(link.category)}</TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={link.is_published}/>
                </TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={link.is_featured}/>
                </TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  {/* TODO: Convert to component */}
                  <Dropdown>
                    {[
                      {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/links/${link.id}/edit`)},
                      {name: 'Reorder', icon: SortAscendingIcon, onClick: () => setModalAction({link, action: 'reorder'})},
                      {name: 'Delete', icon: TrashIcon, onClick: () => setModalAction({link, action: 'delete'})},
                    ]}
                  </Dropdown>
                </TableData>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="mt-12">
        <h2 className="mb-2 text-lg leading-6 font-medium text-gray-900">
          Screen-Specific Links
        </h2>

        {/* Screen-Specific Links */}
        <Table paginationData={screenLinks} setPage={setScreenLinksPage}>
          <TableHead>
            <TableRow>
              <TableHeader>Text</TableHeader>
              <TableHeader>Link To</TableHeader>
              <TableHeader>Category</TableHeader>
              <TableHeader alignment={TableHeader.alignments.CENTER}>Published</TableHeader>
              <TableHeader/>
            </TableRow>
          </TableHead>

          <TableBody isLoading={isLoadingScreenLinks}>

            {/* Empty */}
            {(!screenLinks || screenLinks.links.length < 1) &&
              <TableRow index={0}>
                <TableData colSpan={5}>
                  <div className="py-4 flex flex-col justify-center items-center">
                    <LinkIcon className="h-8 w-8 text-gray-400 mb-2"/>
                    <span className="text-sm font-medium text-gray-600">No links</span>
                  </div>
                </TableData>
              </TableRow>
            }

            {/* Link Rows */}
            {screenLinks?.data.map((link, index) => (
              <TableRow key={link.id} index={index} highlight>
                <TableData>
                  <div className="flex">
                    <div className="items-center absolute h-full left-[0.35rem] top-0 flex duration-200 transition-opacity group-hover:opacity-100 opacity-0">
                      {/* TODO: Convert to component */}
                      <Dropdown position="left">
                        {[
                          {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/links/${link.id}/edit`)},
                          {name: 'Delete', icon: TrashIcon, onClick: () => setModalAction({link, action: 'delete'})},
                        ]}
                      </Dropdown>
                    </div>

                    <div className="flex group-hover:translate-x-1 transition-all">
                      {link.icon ?
                        <Icon name={link.icon} className="h-5 w-5 mr-2 text-gray-500"/> :
                        <LinkIcon className="h-5 w-5 mr-2 text-gray-500"/>
                      }
                      {truncate(link.text, 35)}
                    </div>
                  </div>
                </TableData>
                <TableData>{urlDisplay(link.url, termFor)}</TableData>
                <TableData>{categoryName(link.category)}</TableData>
                <TableData alignment={TableData.alignments.CENTER}>
                  <BooleanIndicator value={link.is_published}/>
                </TableData>
                <TableData alignment={TableData.alignments.RIGHT}>
                  {/* TODO: Convert to component */}
                  <Dropdown>
                    {[
                      {name: 'Edit', icon: PencilIcon, onClick: () => navigate(`/links/${link.id}/edit`)},
                      {name: 'Delete', icon: TrashIcon, onClick: () => setModalAction({link, action: 'delete'})},
                    ]}
                  </Dropdown>
                </TableData>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {/* Link reordering modal */}
      <ReorderLink
        link={modalAction?.link ?? null}
        show={modalAction?.action === 'reorder'}
        onCancel={() => setModalAction(null)}
        onReorder={() => setModalAction(null)}
        onReorderFail={() => setModalAction(null)}
      />

      {/* Delete confirmation modal */}
      <DeleteLink
        link={modalAction?.link ?? null}
        show={modalAction?.action === 'delete'}
        onCancel={() => setModalAction(null)}
        onDelete={() => setModalAction(null)}
        onDeleteFail={() => setModalAction(null)}
      />
    </Page>
  );
}